import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, RefreshCw, Layers, ChevronDown, ChevronUp,
  Crosshair, CircleDot, Hash, Award, TrendingUp, Search,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';

const POS_LABELS = ['1ra', '2da', '3ra'];

const NumberBall = ({ number, size = 'md', highlight = false, position = null }) => {
  const sizeClass = size === 'lg' ? 'w-12 h-12 text-xl' : size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-base';
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div
        className={`${sizeClass} rounded-full flex items-center justify-center font-bold transition-all ${
          highlight
            ? 'bg-gradient-to-br from-cyan-400 to-blue-600 text-white shadow-lg shadow-cyan-500/30'
            : 'bg-slate-800 text-white border border-slate-700'
        }`}
        data-testid={`number-ball-${number}`}
      >
        {String(number).padStart(2, '0')}
      </div>
      {position !== null && (
        <span className="text-[9px] text-slate-500 font-medium">{POS_LABELS[position]}</span>
      )}
    </div>
  );
};

const ConfidenceBar = ({ value, label }) => {
  const color = value >= 85 ? 'bg-cyan-400' : value >= 70 ? 'bg-blue-500' : 'bg-amber-500';
  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-500 text-[10px] w-12">{label}</span>
      <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${value}%` }} />
      </div>
      <span className="text-white text-xs font-bold w-10 text-right">{value}%</span>
    </div>
  );
};

const PaleCard = ({ combo, rank, lastDraw }) => {
  const lastSet = new Set(lastDraw);
  const lastTerms = new Set(lastDraw.map(n => n % 10));
  return (
    <div className="p-3 bg-slate-800/40 rounded-lg border border-slate-700/40 hover:border-cyan-900/40 transition-all" data-testid={`pale-${rank}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-cyan-400 font-bold text-sm">#{rank}</span>
          <div className="flex gap-2">
            {combo.numbers.map((n, i) => (
              <NumberBall key={`nb-${n}`} number={n} highlight={lastTerms.has(n % 10)} />
            ))}
          </div>
        </div>
        <Badge variant="outline" className="text-cyan-400 border-cyan-500/30 text-xs">
          {combo.score} pts
        </Badge>
      </div>
      <div className="flex gap-3 text-[10px] text-slate-500">
        {combo.pair_history > 0 && (
          <span className="flex items-center gap-0.5">
            <Hash size={10} className="text-amber-400" />
            <span>Historial: <strong className="text-amber-300">{combo.pair_history}x</strong></span>
          </span>
        )}
        {combo.terminal_match > 0 && (
          <span className="flex items-center gap-0.5">
            <CircleDot size={10} className="text-cyan-400" />
            <span>Term: <strong className="text-cyan-300">{combo.terminal_match}</strong></span>
          </span>
        )}
        {combo.neighbors > 0 && (
          <span className="flex items-center gap-0.5">
            <Crosshair size={10} className="text-emerald-400" />
            <span>Vecinos: <strong className="text-emerald-300">{combo.neighbors}</strong></span>
          </span>
        )}
      </div>
    </div>
  );
};

const TripletaCard = ({ combo, rank, lastDraw }) => {
  const lastTerms = new Set(lastDraw.map(n => n % 10));
  return (
    <div className="p-3 bg-slate-800/40 rounded-lg border border-slate-700/40 hover:border-purple-900/40 transition-all" data-testid={`tripleta-${rank}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-purple-400 font-bold text-sm">#{rank}</span>
          <div className="flex gap-2">
            {combo.numbers.map((n, i) => (
              <NumberBall key={`nbp-${n}-${i}`} number={n} position={i} highlight={lastTerms.has(n % 10)} />
            ))}
          </div>
        </div>
        <Badge variant="outline" className="text-purple-400 border-purple-500/30 text-xs">
          {combo.score} pts
        </Badge>
      </div>
      <div className="flex gap-3 text-[10px] text-slate-500">
        <span>Cadena: <strong className="text-purple-300">{combo.chain_score}</strong></span>
        {combo.terminal_match > 0 && (
          <span>Term: <strong className="text-cyan-300">{combo.terminal_match}</strong></span>
        )}
        {combo.neighbors > 0 && (
          <span>Vec: <strong className="text-emerald-300">{combo.neighbors}</strong></span>
        )}
        <span>Suma: <strong className="text-slate-300">{combo.sum}</strong></span>
      </div>
    </div>
  );
};

const LotteryDetail = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  if (!data) return null;

  return (
    <div className="rounded-xl bg-slate-900/70 border border-slate-800/50 overflow-hidden" data-testid={`lottery-${data.lottery_id}`}>
      <button
        className="w-full p-4 flex items-center justify-between hover:bg-slate-800/30 transition-all"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
            <Layers size={18} className="text-cyan-400" />
          </div>
          <div className="text-left">
            <h3 className="text-white font-bold text-sm">{data.lottery_name}</h3>
            <span className="text-slate-500 text-xs">{data.draws_analyzed} sorteos</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="flex gap-2">
              {data.top_pale && (
                <span className="text-cyan-400 text-xs font-bold">
                  Pale: {data.top_pale.numbers.join('-')}
                </span>
              )}
              {data.top_tripleta && (
                <span className="text-purple-400 text-xs font-bold">
                  Trip: {data.top_tripleta.numbers.join('-')}
                </span>
              )}
            </div>
            <div className="flex gap-2 justify-end mt-0.5">
              <span className="text-[10px] text-cyan-400/60">{data.pale_confidence}%</span>
              <span className="text-[10px] text-purple-400/60">{data.tripleta_confidence}%</span>
            </div>
          </div>
          {expanded ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
        </div>
      </button>

      {expanded && <LotteryExpanded lotteryId={data.lottery_id} lotteryName={data.lottery_name} />}
    </div>
  );
};

const LotteryExpanded = ({ lotteryId, lotteryName }) => {
  const { token } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await axios.get(`${API}/pale-tripleta/generate/${lotteryId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDetail(res.data);
      } catch (e) {
        toast.error('Error cargando detalle');
      }
      setLoading(false);
    };
    fetchDetail();
  }, [lotteryId, token]);

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <Loader2 className="animate-spin text-cyan-400" size={20} />
      </div>
    );
  }

  if (!detail) return null;

  return (
    <div className="px-4 pb-4 border-t border-slate-800/50">
      <div className="flex items-center gap-2 mt-3 mb-2 text-xs text-slate-500">
        <span>Ultimo sorteo:</span>
        <div className="flex gap-1">
          {detail.last_draw.map((n, i) => (
            <NumberBall key={`nbs-${n}-${i}`} number={n} size="sm" position={i} />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
        {/* Palé */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-cyan-400 font-bold text-sm flex items-center gap-1">
              <Award size={14} />
              Pale
            </h4>
            <span className="text-cyan-400/60 text-xs">{detail.pale.confidence}% confianza</span>
          </div>
          <ConfidenceBar value={detail.pale.confidence} label="Conf." />
          <div className="space-y-2 mt-2">
            {detail.pale.combinations.map((c, i) => (
              <PaleCard key={`pale-${c.numbers?.join("-") || i}`} combo={c} rank={i + 1} lastDraw={detail.last_draw} />
            ))}
          </div>
        </div>

        {/* Tripleta */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-purple-400 font-bold text-sm flex items-center gap-1">
              <TrendingUp size={14} />
              Tripleta
            </h4>
            <span className="text-purple-400/60 text-xs">{detail.tripleta.confidence}% confianza</span>
          </div>
          <ConfidenceBar value={detail.tripleta.confidence} label="Conf." />
          <div className="space-y-2 mt-2">
            {detail.tripleta.combinations.map((c, i) => (
              <TripletaCard key={`trip-${c.numbers?.join("-") || i}`} combo={c} rank={i + 1} lastDraw={detail.last_draw} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};


export default function PaleTripletaPage() {
  const { user, token } = useAuth();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pale-tripleta/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllData(res.data.analyses || []);
    } catch (e) {
      toast.error('Error cargando combinaciones');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { if (token) fetchAll(); }, [token, fetchAll]);

  const filtered = search
    ? allData.filter(d => d.lottery_name.toLowerCase().includes(search.toLowerCase()))
    : allData;

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <p className="text-slate-400">Inicia sesion para ver combinaciones</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e1a]" data-testid="pale-tripleta-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <ArrowLeft size={16} />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Layers className="text-cyan-400" size={24} />
                Pale & Tripleta
              </h1>
              <p className="text-slate-500 text-sm">Combinaciones basadas en probabilidad por posicion</p>
            </div>
          </div>
          <Button
            variant="outline" size="sm" onClick={fetchAll} disabled={loading}
            className="border-slate-700 text-slate-400 hover:text-white"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        {/* Info banner */}
        <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-cyan-950/40 to-purple-950/40 border border-cyan-900/30">
          <div className="grid grid-cols-2 gap-4 text-xs text-slate-400">
            <div>
              <span className="text-cyan-400 font-bold">Pale:</span> 2 numeros que salen juntos.
              Basado en co-ocurrencia historica + terminales + vecinos.
            </div>
            <div>
              <span className="text-purple-400 font-bold">Tripleta:</span> 3 numeros en posicion exacta.
              Basado en frecuencia posicional + cadena de terminales.
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-4 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Buscar loteria..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-slate-900/70 border-slate-800 text-white pl-10"
            data-testid="search-lottery"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-cyan-400" size={32} />
          </div>
        ) : (
          <div className="space-y-3" data-testid="lottery-list">
            {filtered.map((data) => (
              <LotteryDetail key={data.lottery_id} data={data} />
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No se encontraron loterias
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
